/**
 * Privacy Policy Page
 * Explains data collection, cookies, and user rights
 */

import { Helmet } from 'react-helmet-async'

export function PrivacyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Privacy Policy — BankStatementReader</title>
        <meta name="description" content="How BankStatementReader collects, uses, and protects your data, including cookies and analytics." />
        <link rel="canonical" href="https://bankstatementreader.com/privacy" />
      </Helmet>
      <div className="max-w-4xl mx-auto p-8 py-16">
        <h1 className="text-4xl font-bold mb-8 text-gray-900">Privacy Policy</h1>

        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          <section>
            <p className="text-sm text-gray-600 mb-6">
              <strong>Last Updated:</strong> October 12, 2025
            </p>
            <p className="text-gray-700 leading-relaxed">
              At BankStatementReader, we respect your privacy and are committed to protecting your personal data.
              This privacy policy explains how we collect, use, and protect your information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Cookies & Analytics</h2>
            <p className="mb-3 text-gray-700 leading-relaxed">
              We use Google Analytics to understand how visitors use our site.
              This helps us improve your experience and identify issues.
            </p>
            <div className="bg-indigo-50 border border-indigo-200 rounded p-4 mb-3">
              <p className="font-semibold text-indigo-900 mb-2">Data collected:</p>
              <ul className="list-disc list-inside space-y-1 text-indigo-800">
                <li>Page views and navigation patterns</li>
                <li>Time spent on pages</li>
                <li>Geographic location (city-level only)</li>
                <li>Device type and browser information</li>
                <li>Referring website</li>
              </ul>
            </div>
            <div className="bg-green-50 border border-green-200 rounded p-4">
              <p className="font-semibold text-green-900 mb-2">Your rights:</p>
              <ul className="list-disc list-inside space-y-1 text-green-800">
                <li>You can decline analytics cookies at any time</li>
                <li>Your choice is stored in your browser's localStorage</li>
                <li>No personal data is transmitted without your consent</li>
                <li>You can clear your choice by clearing browser data</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Account Data</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              When you create an account with us (via Clerk authentication), we collect:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
              <li>Email address</li>
              <li>Full name (optional)</li>
              <li>Authentication provider info (Google, Apple, etc.)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              This data is used solely for authentication and account management.
              We do not sell or share your personal data with third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Statement Data</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Bank statements you upload are:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 mb-3">
              <li>Encrypted at rest in Cloudflare R2 storage</li>
              <li>Accessible only to your account</li>
              <li>Processed by our OCR system (PaddleOCR)</li>
              <li>Retained until you delete them</li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              <strong>We never share your financial data.</strong> All processing happens within our secure infrastructure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Data Storage</h2>
            <p className="text-gray-700 leading-relaxed">
              <strong>Cookie consent preferences:</strong> Stored in your browser's localStorage (client-side only).
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Account data:</strong> Stored in Cloudflare D1 database (encrypted).
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Uploaded files:</strong> Stored in Cloudflare R2 (encrypted, region: auto).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Third-Party Services</h2>
            <div className="space-y-3">
              <div className="border-l-4 border-indigo-500 pl-4">
                <p className="font-semibold text-gray-900">Google Analytics</p>
                <p className="text-sm text-gray-600">
                  Web analytics. Privacy Policy:{' '}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline"
                  >
                    Google Privacy Policy
                  </a>
                </p>
              </div>
              <div className="border-l-4 border-indigo-500 pl-4">
                <p className="font-semibold text-gray-900">Clerk</p>
                <p className="text-sm text-gray-600">
                  Authentication provider. Privacy Policy:{' '}
                  <a
                    href="https://clerk.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline"
                  >
                    Clerk Privacy Policy
                  </a>
                </p>
              </div>
              <div className="border-l-4 border-indigo-500 pl-4">
                <p className="font-semibold text-gray-900">Cloudflare</p>
                <p className="text-sm text-gray-600">
                  Infrastructure and security. Privacy Policy:{' '}
                  <a
                    href="https://www.cloudflare.com/privacypolicy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:underline"
                  >
                    Cloudflare Privacy Policy
                  </a>
                </p>
              </div>
              
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Your Rights (GDPR)</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              If you are in the European Union, you have the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Correct inaccurate data</li>
              <li><strong>Erasure:</strong> Request deletion of your data</li>
              <li><strong>Restriction:</strong> Limit how we process your data</li>
              <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
              <li><strong>Object:</strong> Object to processing based on legitimate interests</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              To exercise these rights, email us at{' '}
              <a
                href="mailto:privacy@bankstatementreader.com"
                className="text-indigo-600 hover:underline font-semibold"
              >
                privacy@bankstatementreader.com
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this privacy policy from time to time. We will notify you of any changes
              by updating the "Last Updated" date at the top of this policy.
            </p>
          </section>

          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Questions or concerns about privacy? We're here to help.
            </p>
            <div className="bg-gray-50 rounded p-4">
              <p className="text-gray-900">
                <strong>Email:</strong>{' '}
                <a
                  href="mailto:support@bankstatementreader.com"
                  className="text-indigo-600 hover:underline"
                >
                  support@bankstatementreader.com
                </a>
              </p>
              <p className="text-gray-900 mt-2">
                <strong>Response time:</strong> Within 48 hours
              </p>
            </div>
          </section>
        </div>

        <div className="mt-8 text-center">
          <a
            href="/"
            className="text-indigo-600 hover:underline font-semibold"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
}
